import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import DateTimePicker from '@/components/ui/molecules/DateTimePicker';
import { FormattedMessage } from 'react-intl';
import { yupResolver } from '@hookform/resolvers/yup';
import useEpisodePublicationDateFormSchema from './useEpisodePublicationDateFormSchema.hook';
import useUserQuery from '@/queries/user/useUserQuery.hook';
import Text from '@/components/ui/atoms/Text';
import Stack from '@/components/ui/layout/Stack';
import styled from 'styled-components';
import Button from '@/components/ui/atoms/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import UiKitTooltip from '@/components/ui/atoms/UiKitTooltip';
import Spinner from '@/components/ui/atoms/Spinner';
import Alert from '@/components/ui/atoms/Alert';
import { formatDateForApi } from '@/helpers';
import useTimezonesQuery from '@/queries/timezone/useTimezonesQuery.hook';
import { useEpisodeBeingEdited } from '../../organisms/EpisodeEditForm/EpisodeEditFormContext';
import { EPISODE_STATUS } from '@/utils/constants';
import PublishButton from '../../organisms/EpisodeEditForm/actions/PublishButton';

const EpisodePublicationDateForm = ({
    publishedAt,
    isOnline,
    isLoading,
    handleEditPublicationDate,
    onCloseModal,
}) => {
    const { data: user } = useUserQuery();
    const timezones = useTimezonesQuery();
    const schema = useEpisodePublicationDateFormSchema();
    const { handleSubmit, formState, setValue } = useForm({
        mode: 'onChange',
        defaultValues: {
            publishedAt: publishedAt || '',
        },
        resolver: yupResolver(schema),
    });
    const { isValid, isDirty } = formState;
    const timezone = timezones?.data?.[user?.timezone]?.split(' ')?.reverse()?.join(' ');
    const episode = useEpisodeBeingEdited();
    const isScheduled = episode?.state === EPISODE_STATUS.SCHEDULED;

    return (
        <Form onSubmit={handleSubmit(handleEditPublicationDate)}>
            <Stack $gap="0.5rem">
                <DateTimePicker
                    value={publishedAt}
                    onChange={(value) =>
                        setValue('publishedAt', formatDateForApi(value), {
                            shouldValidate: true,
                            shouldDirty: true,
                        })
                    }
                    dateLabel={<FormattedMessage defaultMessage="Jour" />}
                    timeLabel={<FormattedMessage defaultMessage="Heure" />}
                />
                <TimezoneWrapper>
                    {timezone ? (
                        <Text variant="bodyM" color="--neutral500">
                            {timezones?.data?.[user?.timezone]?.split(' ')?.reverse()?.join(' ')}
                        </Text>
                    ) : (
                        <Spinner />
                    )}
                    <Tooltip
                        variant="dark"
                        position="bottom"
                        content={
                            <FormattedMessage defaultMessage="La modification du fuseau horaire de publication est accessible depuis votre compte." />
                        }
                    >
                        <TooltipIcon icon={icon({ name: 'info-circle', style: 'solid' })} />
                    </Tooltip>
                </TimezoneWrapper>
            </Stack>
            {!isOnline && (
                <Alert
                    variant="info"
                    icon={<InfoIcon icon={icon({ name: 'info-circle', style: 'solid' })} />}
                >
                    <Text color="--info500">
                        <FormattedMessage defaultMessage="L’épisode sera publié automatiquement à la date et à l’heure choisies." />
                    </Text>
                </Alert>
            )}
            <ButtonsWrapper>
                <ButtonsInner>
                    <Button type="submit" isLoading={isLoading} isDisabled={!isValid || !isDirty}>
                        {isOnline ? (
                            <FormattedMessage defaultMessage="Modifier" />
                        ) : (
                            <FormattedMessage defaultMessage="Planifier" />
                        )}
                    </Button>
                    {isScheduled && <PublishNowButton />}
                </ButtonsInner>
                <CancelButton onPress={onCloseModal} variant="ghost">
                    <FormattedMessage defaultMessage="Annuler" />
                </CancelButton>
            </ButtonsWrapper>
        </Form>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    row-gap: 1.5rem;
`;
const ButtonsWrapper = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    align-items: stretch;

    ${(p) => p.theme.mediaQueries.tabletAndUp} {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;

        & > :first-child {
            order: 1;
        }
    }
`;
const ButtonsInner = styled.div`
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    row-gap: 0.5rem;
    align-items: stretch;

    ${(p) => p.theme.mediaQueries.tabletAndUp} {
        flex-direction: row;
        column-gap: 0.5rem;
        align-items: center;
        flex-grow: 0;

        & > :first-child {
            order: 1;
        }
    }
`;
const CancelButton = styled(Button)`
    padding: 0;
    color: var(--neutral500);
`;
const TimezoneWrapper = styled.div`
    display: flex;
    align-items: center;
    column-gap: 0.25rem;
`;
const TooltipIcon = styled(FontAwesomeIcon)`
    color: var(--neutral500);
    width: 0.75rem;
    height: 0.75rem;
`;
const Tooltip = styled(UiKitTooltip)`
    z-index: 1;
`;
const InfoIcon = styled(FontAwesomeIcon)`
    color: var(--info500);
    width: 1.25rem;
    height: 1.25rem;
`;
const PublishNowButton = styled(PublishButton)`
    background-color: var(--primary50);
    color: var(--primary);
    box-shadow: none;
`;

EpisodePublicationDateForm.propTypes = {
    publishedAt: PropTypes.string,
    isOnline: PropTypes.bool,
    isLoading: PropTypes.bool,
    handleEditPublicationDate: PropTypes.func,
    onCloseModal: PropTypes.func,
};

export default EpisodePublicationDateForm;
